/**
 * Xuất SKKN ra PDF (văn bản Unicode, không dùng ảnh chụp) và DOCX có cấu trúc — đồng bộ nội dung với bản preview.
 */
import jsPDF from 'jspdf';
import {
  Document as DocxDocument,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  VerticalAlign,
} from 'docx';
import { buildDocxFooterWithPageNumbers } from './docxFooter';
import type { SKKNData } from '../data/skknSampleData';
import { embedInterFontForPdf } from './pdfUnicodeFont';

function blankOr(v: string | undefined | null, fallback = '(Chưa điền)') {
  const t = (v || '').trim();
  return t || fallback;
}

export async function exportSkknPdf(opts: {
  data: SKKNData;
  qualityShortLabel: string;
  filename: string;
  applyWatermark?: (pdf: jsPDF) => void;
}): Promise<void> {
  const { data, qualityShortLabel, filename, applyWatermark } = opts;
  const doc = new jsPDF('p', 'mm', 'a4');
  const fontFamily = (await embedInterFontForPdf(doc)) ? 'Inter' : 'helvetica';
  const margin = 15;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;

  let y = 16;

  const ensureSpace = (needMm: number) => {
    if (y + needMm > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const textRgb = (r: number, g: number, b: number) => doc.setTextColor(r, g, b);
  const lineH = (size: number) => size * 0.52;

  const addText = (txt: string, size: number, opts: { bold?: boolean; center?: boolean; color?: [number, number, number] } = {}) => {
    const { bold = false, center = false, color = [17, 24, 39] } = opts;
    doc.setFont(fontFamily, bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    textRgb(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(txt || '', maxW);
    const lh = lineH(size);
    lines.forEach((line) => {
      ensureSpace(lh + 1);
      if (center) {
        doc.text(line, pageW / 2, y, { align: 'center' });
      } else {
        doc.text(line, margin, y);
      }
      y += lh;
    });
  };

  const addParagraph = (txt: string) => {
    addText(txt, 11, {});
    y += 2;
  };

  const addSectionTitle = (text: string) => {
    y += 3;
    addText(text, 13, { bold: true, color: [14, 116, 109] });
    y += 2;
  };

  const addSubTitle = (text: string) => {
    y += 2;
    addText(text, 11, { bold: true });
    y += 1;
  };

  addText('SÁNG KIẾN KINH NGHIỆM', 9, { bold: true, center: true, color: [14, 116, 109] });
  y += 1;
  addText(blankOr(data.title, '[Tên đề tài SKKN]'), 16, { bold: true, center: true });
  y += 2;
  addText(
    `Môn: ${blankOr(data.subject, '—')} | ${blankOr(data.gradeLevel, '—')} | Năm học: ${blankOr(data.academicYear, '—')}`,
    10,
    { center: true }
  );
  addText(`Tác giả: ${blankOr(data.author, '—')} | ${blankOr(data.school, '—')}`, 10, { center: true });
  y += 2;
  addText(
    `Chuẩn trình bày: ${qualityShortLabel} · Khung nội dung I–VII`,
    8,
    { center: true, color: [100, 116, 139] }
  );
  y += 6;

  addSectionTitle('I. PHẦN MỞ ĐẦU (ĐẶT VẤN ĐỀ)');
  addSubTitle('1. Lý do chọn đề tài');
  addParagraph(blankOr(data.part1.reason));
  addSubTitle('2. Mục tiêu nghiên cứu');
  const objs = (data.part1.objectives || []).filter(Boolean);
  if (objs.length) objs.forEach((o) => addParagraph(`• ${o}`));
  else addParagraph('(Chưa điền)');
  addSubTitle('3. Đối tượng và phạm vi');
  addParagraph(`Đối tượng: ${blankOr(data.part1.scope.target)}`);
  addParagraph(`Thời gian: ${blankOr(data.part1.scope.duration)}`);
  addParagraph(`Địa điểm: ${blankOr(data.part1.scope.location)}`);
  addSubTitle('4. Phương pháp nghiên cứu');
  addParagraph((data.part1.methods || []).join(', ') || '(Chưa điền)');

  addSectionTitle('II. CƠ SỞ LÝ LUẬN VÀ THỰC TIỄN');
  addSubTitle('1. Cơ sở lý luận');
  addParagraph(blankOr(data.part2.theory));
  addSubTitle('2. Thực trạng trước khi áp dụng');
  addParagraph(blankOr(data.part2.currentState));
  if (data.part2.surveyData?.trim()) {
    addSubTitle('3. Số liệu khảo sát ban đầu');
    addParagraph(data.part2.surveyData);
  }

  addSectionTitle('III. NỘI DUNG SÁNG KIẾN');
  addParagraph(`Nhóm giải pháp: ${blankOr(data.part3.solutionGroup, '(Chưa chọn)')}`);
  const sols = (data.part3.solutions || []).filter((s) => s.name || s.coreIdea);
  sols.forEach((sol, i) => {
    y += 2;
    addText(sol.name || `Giải pháp ${i + 1}`, 11, { bold: true });
    addParagraph(`a) Ý tưởng cốt lõi: ${blankOr(sol.coreIdea)}`);
    addParagraph(`b) Quy trình:\n${blankOr(sol.process)}`);
    addParagraph(`c) Ví dụ minh họa: ${blankOr(sol.example)}`);
  });

  addSectionTitle('IV. KẾT QUẢ ĐẠT ĐƯỢC');
  const rows = (data.part4.comparisonTable || []).filter((r) => r.criterion?.trim());
  if (rows.length) {
    rows.forEach((r) => {
      addParagraph(
        `• ${r.criterion}: Trước ${blankOr(r.before, '—')} | Sau ${blankOr(r.after, '—')} | ${blankOr(r.change, '—')}`
      );
    });
  } else addParagraph('(Chưa điền)');
  if (data.part4.chartsNote?.trim()) {
    addSubTitle('Ghi chú biểu đồ');
    addParagraph(data.part4.chartsNote);
  }

  addSectionTitle('V. KHẢ NĂNG NHÂN RỘNG');
  addParagraph(`1. Môn khác: ${blankOr(data.part5.otherSubjects)}`);
  addParagraph(`2. Cấp học khác: ${blankOr(data.part5.otherGrades)}`);
  addParagraph(`3. Điều kiện đặc biệt: ${blankOr(data.part5.specialConditions)}`);

  addSectionTitle('VI. BÀI HỌC KINH NGHIỆM');
  const listBlock = (label: string, items: string[]) => {
    addSubTitle(label);
    const f = (items || []).filter(Boolean);
    if (f.length) f.forEach((s) => addParagraph(`• ${s}`));
    else addParagraph('(Chưa điền)');
  };
  listBlock('Điều làm tốt', data.part6.successes || []);
  listBlock('Điều cần cải tiến', data.part6.improvements || []);
  listBlock('Yếu tố quyết định thành công', data.part6.successFactors || []);

  addSectionTitle('VII. KẾT LUẬN VÀ KIẾN NGHỊ');
  addParagraph(blankOr(data.part7.summary));
  addParagraph(`Đề xuất Nhà trường: ${blankOr(data.part7.recommendations.school)}`);
  addParagraph(`Đề xuất Phòng GD: ${blankOr(data.part7.recommendations.department)}`);
  addParagraph(`Đề xuất Đồng nghiệp: ${blankOr(data.part7.recommendations.colleagues)}`);

  if (applyWatermark) applyWatermark(doc);

  doc.save(`${filename}.pdf`);
}

export async function exportSkknDocx(opts: {
  data: SKKNData;
  qualityShortLabel: string;
  filename: string;
  withFooterWatermark: boolean;
}): Promise<void> {
  const { data, qualityShortLabel, filename, withFooterWatermark } = opts;
  const children: (Paragraph | Table)[] = [];

  const push = (p: Paragraph | Table) => children.push(p);

  push(
    new Paragraph({
      children: [new TextRun({ text: 'SÁNG KIẾN KINH NGHIỆM', bold: true, color: '0F766E', size: 24 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 120 },
    })
  );
  push(
    new Paragraph({
      children: [new TextRun({ text: blankOr(data.title, '[Tên đề tài SKKN]'), bold: true, size: 32 })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    })
  );
  push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Môn: ${blankOr(data.subject, '—')} | ${blankOr(data.gradeLevel, '—')} | Năm học: ${blankOr(data.academicYear, '—')}`,
        }),
      ],
      alignment: AlignmentType.CENTER,
    })
  );
  push(
    new Paragraph({
      children: [new TextRun({ text: `Tác giả: ${blankOr(data.author, '—')} | ${blankOr(data.school, '—')}` })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    })
  );
  push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Chuẩn trình bày: ${qualityShortLabel} · Khung nội dung I–VII`,
          italics: true,
          color: '64748B',
          size: 20,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    })
  );

  const h2 = (t: string) =>
    push(new Paragraph({ text: t, heading: HeadingLevel.HEADING_2, spacing: { before: 240, after: 120 } }));
  const h3 = (t: string) =>
    push(new Paragraph({ children: [new TextRun({ text: t, bold: true })], spacing: { before: 160, after: 80 } }));
  const body = (t: string) => {
    (t || '').split(/\n+/).forEach((line) => {
      if (line.trim()) push(new Paragraph({ text: line.trim(), spacing: { after: 120 } }));
    });
  };

  h2('I. PHẦN MỞ ĐẦU (ĐẶT VẤN ĐỀ)');
  h3('1. Lý do chọn đề tài');
  body(blankOr(data.part1.reason));
  h3('2. Mục tiêu nghiên cứu');
  const objs = (data.part1.objectives || []).filter(Boolean);
  if (objs.length) objs.forEach((o) => push(new Paragraph({ text: o, bullet: { level: 0 }, spacing: { after: 80 } })));
  else push(new Paragraph({ text: '(Chưa điền)', spacing: { after: 120 } }));
  h3('3. Đối tượng và phạm vi');
  body(`Đối tượng: ${blankOr(data.part1.scope.target)}\nThời gian: ${blankOr(data.part1.scope.duration)}\nĐịa điểm: ${blankOr(data.part1.scope.location)}`);
  h3('4. Phương pháp nghiên cứu');
  body((data.part1.methods || []).join(', ') || '(Chưa điền)');

  h2('II. CƠ SỞ LÝ LUẬN VÀ THỰC TIỄN');
  h3('1. Cơ sở lý luận');
  body(blankOr(data.part2.theory));
  h3('2. Thực trạng trước khi áp dụng');
  body(blankOr(data.part2.currentState));
  if (data.part2.surveyData?.trim()) {
    h3('3. Số liệu khảo sát ban đầu');
    body(data.part2.surveyData);
  }

  h2('III. NỘI DUNG SÁNG KIẾN');
  push(
    new Paragraph({
      children: [new TextRun({ text: `Nhóm giải pháp: ${blankOr(data.part3.solutionGroup, '(Chưa chọn)')}`, bold: true })],
      spacing: { after: 120 },
    })
  );
  (data.part3.solutions || [])
    .filter((s) => s.name || s.coreIdea)
    .forEach((sol, i) => {
      push(
        new Paragraph({
          children: [new TextRun({ text: sol.name || `Giải pháp ${i + 1}`, bold: true })],
          spacing: { before: 200, after: 80 },
        })
      );
      push(
        new Paragraph({
          children: [new TextRun({ text: 'a) Ý tưởng cốt lõi: ', bold: true }), new TextRun(blankOr(sol.coreIdea))],
        })
      );
      push(new Paragraph({ children: [new TextRun({ text: 'b) Quy trình:', bold: true })], spacing: { before: 80 } }));
      body(blankOr(sol.process));
      push(
        new Paragraph({
          children: [new TextRun({ text: 'c) Ví dụ minh họa: ', bold: true }), new TextRun(blankOr(sol.example))],
          spacing: { before: 80 },
        })
      );
    });

  h2('IV. KẾT QUẢ ĐẠT ĐƯỢC');
  const tblRows = (data.part4.comparisonTable || []).filter((r) => r.criterion?.trim());
  if (tblRows.length) {
    const header = new TableRow({
      tableHeader: true,
      children: ['Tiêu chí', 'Trước', 'Sau', 'Tăng/giảm'].map(
        (text) =>
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text, bold: true })], alignment: AlignmentType.CENTER })],
            shading: { fill: 'ECFDF5' },
            verticalAlign: VerticalAlign.CENTER,
          })
      ),
    });
    const dataRows = tblRows.map(
      (r) =>
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph(r.criterion)], width: { size: 40, type: WidthType.PERCENTAGE } }),
            new TableCell({ children: [new Paragraph({ text: r.before, alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: r.after, alignment: AlignmentType.CENTER })] }),
            new TableCell({ children: [new Paragraph({ text: r.change, alignment: AlignmentType.CENTER })] }),
          ],
        })
    );
    push(
      new Table({
        rows: [header, ...dataRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
          left: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
          right: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
          insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'auto' },
        },
      })
    );
  } else push(new Paragraph({ text: '(Chưa điền)', spacing: { after: 120 } }));
  if (data.part4.chartsNote?.trim()) {
    h3('Ghi chú biểu đồ');
    body(data.part4.chartsNote);
  }

  h2('V. KHẢ NĂNG NHÂN RỘNG');
  body(`1. Môn khác: ${blankOr(data.part5.otherSubjects)}\n2. Cấp học khác: ${blankOr(data.part5.otherGrades)}\n3. Điều kiện đặc biệt: ${blankOr(data.part5.specialConditions)}`);

  h2('VI. BÀI HỌC KINH NGHIỆM');
  const listDoc = (label: string, items: string[]) => {
    h3(label);
    const f = (items || []).filter(Boolean);
    if (f.length) f.forEach((s) => push(new Paragraph({ text: s, bullet: { level: 0 }, spacing: { after: 80 } })));
    else push(new Paragraph({ text: '(Chưa điền)', spacing: { after: 120 } }));
  };
  listDoc('Điều làm tốt', data.part6.successes || []);
  listDoc('Điều cần cải tiến', data.part6.improvements || []);
  listDoc('Yếu tố quyết định thành công', data.part6.successFactors || []);

  h2('VII. KẾT LUẬN VÀ KIẾN NGHỊ');
  body(blankOr(data.part7.summary));
  body(`Đề xuất Nhà trường: ${blankOr(data.part7.recommendations.school)}`);
  body(`Đề xuất Phòng GD: ${blankOr(data.part7.recommendations.department)}`);
  body(`Đề xuất Đồng nghiệp: ${blankOr(data.part7.recommendations.colleagues)}`);

  const footer = buildDocxFooterWithPageNumbers({ showWatermark: !!withFooterWatermark });

  const docx = new DocxDocument({
    styles: {
      default: {
        document: {
          run: {
            size: 24, // 12pt
            font: 'Times New Roman',
          },
        },
        heading2: {
          run: {
            size: 26, // 13pt
            bold: true,
            font: 'Times New Roman',
          },
          paragraph: {
            spacing: { before: 240, after: 120 },
          },
        },
      },
    },
    sections: [{ children, footers: { default: footer } }],
  });

  const blob = await Packer.toBlob(docx);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.docx`;
  a.click();
  URL.revokeObjectURL(url);
}
